import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import TimeAgo from "react-timeago";
import {
    calculateDateOffset, Time, toInputDateHourMinute, validateMinimumDateAllowed,
} from "../../date";
import Confirm from "../confirm/Confirm";
import TimezoneSelect from "../pickers/timezone/TimezoneSelect";

interface Props {
    date?: Date;
    shouldShow: boolean;
    onSelected: (date: Date, timezone: string, notes: string) => void;
    onCancel: () => void;
    confirmText?: string;
    canAddNotes?: boolean;
    isPresetAutoSelect?: boolean

}

const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default function Scheduler(props: Props): ReactElement | null {
    const [date, setDate] = useState(props.date ?? new Date(new Date().getTime() + 1000 * 60 * 60));
    const [localDate, setLocalDate] = useState(new Date(new Date().getTime() + 1000 * 60 * 60));
    const [timeZoneDate, setTimeZoneDate] = useState(new Date());

    const [notes, setNotes] = useState("");

    const [timeZone, setTimeZone] = useState(LOCAL_TIMEZONE);
    const minDate = Time.getDateInTimeZone(new Date(), timeZone, LOCAL_TIMEZONE);

    const [presets, setPresets] = useState([
        {
            date: new Date(
                minDate.getFullYear(),
                minDate.getMonth(),
                minDate.getDate(),
                minDate.getHours() + 1,
                minDate.getMinutes(),
                minDate.getSeconds(),
            ),
            dayName: "in 1 hour",
            showTime: false,
        },
        {
            date: new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 13, 0, 0),
            dayName: "today",
            showTime: true,
        },
        {
            date: new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() + 1, 9, 0, 0),
            dayName: "tomorrow",
            showTime: true,
        },
    ]);

    useEffect(() => {
        const currentDate = new Date();
        let tempPresets = presets;
        if (currentDate.getDay() === 1 || currentDate.getDay() === 2) {
            tempPresets = tempPresets.filter(preset => preset.dayName !== "thursday");
            tempPresets.push({ date: calculateDateOffset(7, 4, currentDate, 10), dayName: "thursday", showTime: true });
        } else {
            tempPresets = tempPresets.filter(preset => preset.dayName !== "thursday");
        }

        tempPresets = tempPresets.filter(preset => preset.dayName !== "monday");
        setPresets([...tempPresets, { date: calculateDateOffset(7, 1, currentDate, 9), dayName: "monday", showTime: true }]);
    }, []);

    useEffect(() => {
        const offsetDate = Time.getDateInTimeZone(localDate, LOCAL_TIMEZONE, timeZone);
        setDate(offsetDate);
    }, [localDate, timeZone, props.shouldShow]);

    useEffect(() => {
        if (props.date) {
            setDate(props.date);
            setLocalDate(Time.getDateInTimeZone(props.date, LOCAL_TIMEZONE, timeZone));
        }
    }, [props.date?.toISOString()]);

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeZoneDate(Time.getDateInTimeZone(new Date(), timeZone, LOCAL_TIMEZONE));
        }, 10000);
        setTimeZoneDate(Time.getDateInTimeZone(new Date(), timeZone, LOCAL_TIMEZONE));
        return () => {
            clearInterval(timer);
        };
    }, [timeZone]);

    return (
        <Confirm
            shouldShow={props.shouldShow}
            onHide={props.onCancel}
            confirmText={props.confirmText}
            onConfirm={() => {
                props.onSelected(date, timeZone, notes);
            }}
        >
            <div>

                <Form.Group>
                    <Form.Label className="fw-bolder">Time zone</Form.Label>
                    <TimezoneSelect
                        className="text-gray col"
                        isRequired
                        value={timeZone}
                        onChange={selected => {
                            setTimeZone(selected);
                        }}
                    />
                    {timeZone !== LOCAL_TIMEZONE && (
                        <Form.Text className="d-block">
                            Local time in
                            {" "}
                            {timeZone}
                            :
                            {" "}
                            {timeZoneDate.toLocaleString(undefined, {
                                year: "numeric",
                                day: "numeric",
                                month: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                            })}
                        </Form.Text>
                    )}
                </Form.Group>

                <Form.Group className="mt-4">
                    <Form.Label className="fw-bolder">Presets</Form.Label>

                    <div className="d-flex flex-wrap">
                        {presets.map(presetDate => (
                            presetDate.date.getTime() < minDate.getTime() ? undefined : (
                                <button
                                    key={presetDate.date.getTime()}
                                    className={`btn btn-outline text-gray-600 mb-1
                                        ${presetDate.date.getTime() === localDate?.getTime() ? "border-primary" : ""}`}
                                    type="button"
                                    onClick={() => {
                                        if (props.isPresetAutoSelect) {
                                            props.onSelected(presetDate.date, timeZone, notes);
                                        } else {
                                            setLocalDate(presetDate.date);
                                        }
                                    }}
                                >
                                    {presetDate.showTime
                                        ? `${(presetDate.date.toLocaleTimeString(undefined, {
                                            hour: "numeric",
                                            minute: "numeric",
                                        }))} `
                                        : ("")}
                                    {presetDate.dayName}
                                </button>
                            )

                        ))}

                    </div>
                </Form.Group>
                <Form.Group className="mt-4">
                    <Form.Label className="fw-bolder">Scheduled Time</Form.Label>
                    <Form.Text className="d-block">
                        What time do you want to schedule?
                    </Form.Text>
                    <input
                        type="datetime-local"
                        className="form-control w-100 mb-2 col"
                        value={toInputDateHourMinute(localDate)}
                        onChange={evt => {
                            const dateValue = new Date(`${evt.target.value}:00`);
                            if (!Number.isNaN(dateValue.getTime())) {
                                setLocalDate(dateValue);
                            }
                        }}
                        min={toInputDateHourMinute(minDate)}
                        onBlur={() => setLocalDate(validateMinimumDateAllowed(minDate, localDate))}
                    />

                </Form.Group>

                <div className="d-flex justify-content-between align-items-end">
                    <small className="text-gray-500">
                        <TimeAgo date={date} title={`${date.toLocaleString()} in ${LOCAL_TIMEZONE}`} />
                    </small>
                </div>

                {props.canAddNotes && (
                    <Form.Group className="mt-4">
                        <Form.Label className="fw-bolder">
                            Notes
                            {" "}
                            <small className="text-light">(optional)</small>
                        </Form.Label>
                        <Form.Text className="d-block">
                            Any notes you want to add before scheduling
                        </Form.Text>
                        <Form.Control
                            as="textarea"
                            value={notes}
                            onChange={evt => setNotes(evt.target.value)}
                        />
                    </Form.Group>

                )}

            </div>
        </Confirm>
    );
}

Scheduler.defaultProps = {
    date: undefined,
    confirmText: "Schedule",
    canAddNotes: false,
    isPresetAutoSelect: true,
};
